import type { Integration } from "@repo/api";
import { Chip, Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import {
  formatDate,
  getImportTimeDisplay,
  getMetadataValue,
  getStatusDisplay,
  SidePanelPlainRow,
} from "../../IntegrationSidePanel";

interface AWSIntegrationSidePanelContentProps {
  integration: Integration;
  credentials?: {
    data?: {
      roleArn?: string;
      externalId?: string;
    };
  };
  isLoadingCredentials: boolean;
  isCredentialsError: boolean;
}

export function AWSIntegrationSidePanelContent({
  integration,
  credentials,
  isLoadingCredentials,
  isCredentialsError,
}: AWSIntegrationSidePanelContentProps) {
  const metadata = integration.metadata || {};
  const features = integration.features || [];
  const statusDisplay = getStatusDisplay(integration.connectionStatus);
  const importTimeDisplay = getImportTimeDisplay(integration.features);
  const statusColor =
    statusDisplay === "Active"
      ? "success"
      : statusDisplay === "Pending"
        ? "warning"
        : "critical";

  const regionsStr =
    features
      .map((f) => f.featureConfig?.regions)
      .filter(Boolean)
      .join(", ") || undefined;
  const regionsList = regionsStr
    ? regionsStr === "All"
      ? []
      : regionsStr
          .split(",")
          .map((r) => r.trim())
          .filter(Boolean)
    : [];

  return (
    <div className="flex flex-col gap-4 w-full Integrations-sidePanel-summaryContent">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <Typography kind="body3" color="--color-foreground-neutral-subtle">
            <FormattedMessage
              id="s2y5eG"
              defaultMessage="Status"
              description="Status label"
            />
          </Typography>
          <div>
            <Chip
              label={statusDisplay}
              color={statusColor}
              kind="subtleFilled"
              size="small"
            />
          </div>
        </div>
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="ExNLWm"
              defaultMessage="Application"
              description="Application label"
            />
          }
          value={integration.integrationType}
        />
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="QMyMOI"
              defaultMessage="Description"
              description="Description label"
            />
          }
          value={getMetadataValue(metadata, "description")}
        />
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="Vuk3cq"
              defaultMessage="Created on"
              description="Created on label"
            />
          }
          value={formatDate(integration.createdDate)}
        />
      </div>

      <div className="flex flex-col gap-4 p-4 border-t border-[var(--color-border-secondary-decorative,#f2f4f8)]">
        <Typography kind="body1-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="j3KaLq"
            defaultMessage="Connection Details"
            description="Connection details label"
          />
        </Typography>
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="IIHu0w"
              defaultMessage="AWS Account ID"
              description="AWS Account ID label"
            />
          }
          value={getMetadataValue(metadata, "accountId")}
        />
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="TiFaRF"
              defaultMessage="Role ARN"
              description="Role ARN label"
            />
          }
          value={credentials?.data?.roleArn}
          isLoading={isLoadingCredentials}
          isError={isCredentialsError}
        />
      </div>

      <div className="flex flex-col p-4 border-t">
        <Typography kind="body1-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="w77dtq"
            defaultMessage="Advanced Settings"
            description="Advanced settings label"
          />
        </Typography>
        <div className="flex flex-col gap-1">
          <Typography
            kind="body3"
            color="--color-foreground-neutral-subtle"
            className="pb-2"
          >
            <FormattedMessage
              id="vW4qcP"
              defaultMessage="Region to Scan"
              description="Region to scan label"
            />
          </Typography>
          {regionsStr === "All" || regionsList.length === 0 ? (
            <Typography
              kind="body2"
              color="--color-foreground-secondary-bold"
              className="pb-3"
            >
              <FormattedMessage
                id="F7aeoi"
                defaultMessage="All"
                description="All regions text"
              />
            </Typography>
          ) : (
            <div className="flex flex-wrap gap-2 pb-3">
              {regionsList.map((region) => (
                <Chip
                  key={region}
                  size="xSmall"
                  label={region}
                  kind="outlined"
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Typography kind="body3" color="--color-foreground-neutral-subtle">
            <FormattedMessage
              id="+M6hy4"
              defaultMessage="Import Time (UTC)"
              description="Schedule import time label"
            />
          </Typography>
          <Typography kind="body2" color="--color-foreground-secondary-bold">
            {importTimeDisplay}
          </Typography>
        </div>
      </div>
    </div>
  );
}
